import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  SlidersHorizontal,
  Copy,
  User,
  Plus,
  Calendar,
} from "lucide-react";
import {
  listTenants,
  type CurrentUser,
} from "../../lib/api";

const DEMO_TRANSACTIONS = [
  { id: "#a7rz1b", to_user: "RDGOHEL", to_phone: "917900135178", instance: "clvyterfyxfqb...", client_type: "User", before: "0 credits", value: "1000 credits", after: "1000 credits", time: "19/08/2026 14:00" },
  { id: "#exuun0", to_user: "capler", to_phone: "9172198695...", instance: "cmfs9yywsk...", client_type: "User", before: "0 credits", value: "1000 credits", after: "1000 credits", time: "17/08/2026 18:26" },
  { id: "#to5qbq", to_user: "fid", to_phone: "9188800037...", instance: "cm8781ior17...", client_type: "User", before: "0 credits", value: "1000 credits", after: "1000 credits", time: "07/08/2026 12:17" },
  { id: "#g62h4v", to_user: "EXPOZY", to_phone: "9194499068...", instance: "cm2g79ds4a...", client_type: "User", before: "0 credits", value: "100 credits", after: "100 credits", time: "01/08/2026 14:54" },
  { id: "#taqaq4", to_user: "sairam", to_phone: "9193265777...", instance: "clvytrxfrexi64...", client_type: "User", before: "0 credits", value: "1000 credits", after: "1000 credits", time: "01/08/2026 13:17" },
  { id: "#sl0lla", to_user: "fid", to_phone: "9188800037...", instance: "cm8781ior17...", client_type: "User", before: "0 credits", value: "100 credits", after: "100 credits", time: "30/07/2026 20:32" },
  { id: "#te4yy1", to_user: "zoop", to_phone: "9191366797...", instance: "—", client_type: "Super User", before: "0 credits", value: "100 credits", after: "100 credits", time: "25/06/2026 10:43" },
  { id: "#u05ujh", to_user: "gadda", to_phone: "919037184194", instance: "cmfc7vako2y...", client_type: "User", before: "0 credits", value: "10000 credits", after: "10000 credits", time: "08/06/2026 15:33" },
];

export function ManualBillingView({ token, user: _user }: { token: string; user: CurrentUser }) {
  const isSuperAdmin = _user.role === "superadmin" || _user.role === "platform_admin";
  const [searchQuery, setSearchQuery] = useState("");
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  const tenantsQuery = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: () => listTenants(token),
    enabled: isSuperAdmin,
  });

  const filteredTransactions = DEMO_TRANSACTIONS.filter(
    (tx) =>
      tx.to_user.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tx.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tx.to_phone.includes(searchQuery)
  );

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  return (
    <div className="space-y-5">
      {/* Header (Matches 07_recharge_history.png) */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Transaction History</h1>
          <p className="text-xs text-slate-500 mt-1">View and manage all transaction history</p>
        </div>

        {isSuperAdmin && (
          <button
            onClick={() => setIsReceiptModalOpen(true)}
            className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Credit Recharge</span>
          </button>
        )}
      </div>

      {/* Filter Toolbar (Matches 07_recharge_history.png Filter Bar) */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search transactions..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button className="filter-chip">
            <span>⊕ To User</span>
          </button>

          <button className="filter-chip">
            <span>⊕ Date range</span>
          </button>
        </div>

        <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
          <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
          <span>View</span>
        </button>
      </div>

      {/* Transaction History Table (Exact Columns and Layout from 07_recharge_history.png) */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>To User</th>
                <th>Instance</th>
                <th>Client Type</th>
                <th>Before</th>
                <th>Value</th>
                <th>After</th>
                <th>Transaction Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-slate-50/70 transition-colors">
                  {/* ID + Copy Icon */}
                  <td>
                    <div className="flex items-center gap-1 font-mono text-xs text-slate-700">
                      <button onClick={() => copyText(tx.id)} className="hover:text-slate-900">
                        <Copy className="w-3 h-3 text-slate-400" />
                      </button>
                      <span className="font-semibold">{tx.id}</span>
                    </div>
                  </td>

                  {/* To User */}
                  <td>
                    <div className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      <div>
                        <div className="font-bold text-slate-900 text-xs">{tx.to_user}</div>
                        <div className="font-mono text-[11px] text-slate-500">{tx.to_phone}</div>
                      </div>
                    </div>
                  </td>

                  {/* Instance */}
                  <td className="font-mono text-xs text-slate-600">{tx.instance}</td>

                  {/* Client Type */}
                  <td>
                    <span className="text-xs text-slate-700 flex items-center gap-1">
                      <User className="w-3 h-3 text-slate-400" /> {tx.client_type}
                    </span>
                  </td>

                  {/* Before */}
                  <td className="font-mono text-xs text-slate-500">{tx.before}</td>

                  {/* Value */}
                  <td className="font-mono text-xs font-bold text-emerald-600">+{tx.value}</td>

                  {/* After */}
                  <td className="font-mono text-xs font-bold text-slate-800">{tx.after}</td>

                  {/* Transaction Time */}
                  <td>
                    <span className="font-mono text-xs text-slate-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-slate-400" /> {tx.time}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Credit Recharge Modal */}
      {isReceiptModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsReceiptModalOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-900 mb-1">Credit Recharge &amp; Allocation</h3>
            <p className="text-xs text-slate-500 mb-4">
              Allocate WhatsApp message credits to an organization or user.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Target Organization *</label>
                <select className="modern-input w-full text-xs">
                  {tenantsQuery.data?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.slug})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Credits Amount *</label>
                <input
                  type="number"
                  placeholder="1000"
                  defaultValue="1000"
                  className="modern-input w-full text-xs font-mono font-bold text-emerald-600"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button className="btn-secondary text-xs" onClick={() => setIsReceiptModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => {
                    alert("Recharged 1,000 credits successfully.");
                    setIsReceiptModalOpen(false);
                  }}
                >
                  Confirm Recharge
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
